import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { useId } from 'react';
import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import { StyleSheet } from 'aphrodite';
import { addStyle, Id, View, useOnMountEffect } from '@khanacademy/wonder-blocks-core';
import { Strut } from '@khanacademy/wonder-blocks-layout';
import { spacing, semanticColor, border, font } from '@khanacademy/wonder-blocks-tokens';
import { LabelMedium, LabelSmall, styles as styles$7 } from '@khanacademy/wonder-blocks-typography';
import { PhosphorIcon } from '@khanacademy/wonder-blocks-icon';
import checkIcon from '@phosphor-icons/core/bold/check-bold.svg';
import minusIcon from '@phosphor-icons/core/bold/minus-bold.svg';

const _excluded$7 = ["checked", "disabled", "error", "groupName", "id", "testId"];
function mapCheckedToAriaChecked(value) {
  switch (value) {
    case true:
      return "true";
    case false:
      return "false";
    default:
      return "mixed";
  }
}
const size$1 = spacing.medium_16;
const checkSize = spacing.small_12;
const StyledInput$2 = addStyle("input");
const CheckboxCore = React.forwardRef(function CheckboxCore(props, ref) {
  const {
      checked,
      disabled,
      error,
      groupName,
      id,
      testId
    } = props,
    sharedProps = _objectWithoutPropertiesLoose(props, _excluded$7);
  const innerRef = React.useRef(null);
  React.useEffect(() => {
    if (innerRef.current != null) {
      innerRef.current.indeterminate = checked == null;
    }
  }, [checked, innerRef]);
  const handleChange = () => {
    return;
  };
  const stateStyles = _generateStyles$1(checked, error);
  const defaultStyle = [sharedStyles$1.inputReset, sharedStyles$1.default, !disabled && stateStyles.default, disabled && sharedStyles$1.disabled];
  const checkboxIcon = React.createElement(PhosphorIcon, {
    color: disabled ? semanticColor.icon.disabled : semanticColor.icon.inverse,
    icon: checked ? checkIcon : minusIcon,
    size: "small",
    style: [sharedStyles$1.checkboxIcon, {
      width: checkSize,
      height: checkSize
    }]
  });
  const ariaChecked = mapCheckedToAriaChecked(checked);
  return React.createElement(React.Fragment, null, React.createElement(StyledInput$2, _extends({}, sharedProps, {
    ref: node => {
      innerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref != null) {
        ref.current = node;
      }
    },
    type: "checkbox",
    "aria-checked": ariaChecked,
    "aria-invalid": error,
    checked: checked != null ? checked : undefined,
    disabled: disabled,
    id: id,
    name: groupName,
    onChange: handleChange,
    style: defaultStyle,
    "data-testid": testId
  })), checked || checked == null ? checkboxIcon : React.createElement(React.Fragment, null));
});
const disabledState$1 = {
  border: semanticColor.action.secondary.disabled.border,
  background: semanticColor.action.secondary.disabled.background
};
const sharedStyles$1 = StyleSheet.create({
  inputReset: {
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none"
  },
  default: {
    height: size$1,
    width: size$1,
    minHeight: size$1,
    minWidth: size$1,
    margin: 0,
    outline: "none",
    boxSizing: "border-box",
    borderStyle: "solid",
    borderWidth: border.width.thin,
    borderRadius: 3
  },
  disabled: {
    cursor: "auto",
    backgroundColor: disabledState$1.background,
    borderColor: disabledState$1.border,
    borderWidth: border.width.thin
  },
  checkboxIcon: {
    position: "absolute",
    pointerEvents: "none",
    margin: (size$1 - checkSize) / 2
  }
});
const styles$6 = {};
const _generateStyles$1 = (checked, error) => {
  const styleKey = `${String(checked)}-${String(error)}`;
  if (styles$6[styleKey]) {
    return styles$6[styleKey];
  }
  const isCheckedOrIndeterminate = checked || checked == null;
  const actionType = error ? "destructive" : "progressive";
  const styleType = isCheckedOrIndeterminate ? "primary" : "secondary";
  const colorAction = semanticColor.action[styleType][actionType];
  const states = {
    default: {
      border: colorAction.default.border,
      background: colorAction.default.background
    },
    error: {
      border: semanticColor.status.critical.foreground,
      background: semanticColor.status.critical.background
    }
  };
  let newStyles = {};
  if (isCheckedOrIndeterminate) {
    newStyles = {
      default: {
        backgroundColor: states.default.background,
        borderColor: states.default.border,
        ":focus-visible": {
          outline: `${border.width.medium} solid ${semanticColor.focus.outer}`,
          outlineOffset: 1
        },
        ":hover": {
          outline: `${border.width.medium} solid ${colorAction.hover.border}`,
          outlineOffset: 1
        },
        ":active": {
          outline: `${border.width.medium} solid ${colorAction.press.border}`,
          outlineOffset: 1,
          background: colorAction.press.background
        }
      }
    };
  } else {
    const currentState = error ? states.error : states.default;
    newStyles = {
      default: {
        backgroundColor: currentState.background,
        borderColor: currentState.border,
        ":focus-visible": {
          backgroundColor: error ? states.error.background : colorAction.hover.background,
          outline: `${border.width.medium} solid ${semanticColor.focus.outer}`,
          outlineOffset: -1
        },
        ":hover": {
          backgroundColor: error ? states.error.background : colorAction.hover.background,
          outline: `${border.width.medium} solid ${colorAction.hover.border}`,
          outlineOffset: -1
        },
        ":active": {
          backgroundColor: colorAction.press.background,
          outline: `${border.width.medium} solid ${colorAction.press.border}`,
          outlineOffset: -1
        }
      }
    };
  }
  styles$6[styleKey] = StyleSheet.create(newStyles);
  return styles$6[styleKey];
};

const _excluded$6 = ["checked", "disabled", "error", "groupName", "id", "testId"];
const StyledInput$1 = addStyle("input");
const RadioCore = React.forwardRef(function RadioCore(props, ref) {
  const handleChange = () => {
    return;
  };
  const {
      checked,
      disabled,
      error,
      groupName,
      id,
      testId
    } = props,
    sharedProps = _objectWithoutPropertiesLoose(props, _excluded$6);
  const stateStyles = _generateStyles(checked, error);
  const defaultStyle = [sharedStyles.inputReset, sharedStyles.default, !disabled && stateStyles.default, disabled && sharedStyles.disabled];
  return React.createElement(React.Fragment, null, React.createElement(StyledInput$1, _extends({}, sharedProps, {
    type: "radio",
    "aria-invalid": error,
    checked: checked != null ? checked : undefined,
    disabled: disabled,
    id: id,
    name: groupName,
    onChange: handleChange,
    style: defaultStyle,
    "data-testid": testId,
    ref: ref
  })), disabled && checked && React.createElement("span", {
    style: disabledChecked
  }));
});
const disabledState = {
  border: semanticColor.action.secondary.disabled.border,
  background: semanticColor.action.secondary.disabled.background
};
const size = 16;
const disabledChecked = {
  position: "absolute",
  top: size / 4,
  left: size / 4,
  height: size / 2,
  width: size / 2,
  borderRadius: border.radius.radius_full,
  backgroundColor: semanticColor.action.primary.disabled.background
};
const sharedStyles = StyleSheet.create({
  inputReset: {
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none"
  },
  default: {
    height: size,
    width: size,
    minHeight: size,
    minWidth: size,
    margin: 0,
    outline: "none",
    boxSizing: "border-box",
    borderStyle: "solid",
    borderWidth: border.width.thin,
    borderRadius: border.radius.radius_full
  },
  disabled: {
    cursor: "auto",
    backgroundColor: disabledState.background,
    borderColor: disabledState.border,
    borderWidth: border.width.thin
  }
});
const styles$5 = {};
const _generateStyles = (checked, error) => {
  const styleKey = `${String(checked)}-${String(error)}`;
  if (styles$5[styleKey]) {
    return styles$5[styleKey];
  }
  const actionType = error ? "destructive" : "progressive";
  const colorAction = semanticColor.action.secondary[actionType];
  const states = {
    unchecked: {
      border: semanticColor.border.strong,
      background: colorAction.default.background
    },
    checked: {
      border: colorAction.default.foreground,
      background: colorAction.default.background
    },
    error: {
      border: semanticColor.status.critical.foreground,
      background: semanticColor.status.critical.background
    }
  };
  let newStyles = {};
  if (checked) {
    newStyles = {
      default: {
        backgroundColor: states.checked.background,
        borderColor: states.checked.border,
        borderWidth: size / 4,
        ":focus-visible": {
          outline: `${border.width.medium} solid ${semanticColor.focus.outer}`,
          outlineOffset: 1
        },
        ":hover": {
          outline: `${border.width.medium} solid ${colorAction.hover.border}`,
          outlineOffset: 1
        },
        ":active": {
          outline: `${border.width.medium} solid ${colorAction.press.border}`,
          outlineOffset: 1,
          borderColor: colorAction.press.border
        }
      }
    };
  } else {
    const currentState = error ? states.error : states.unchecked;
    newStyles = {
      default: {
        backgroundColor: currentState.background,
        borderColor: currentState.border,
        ":focus-visible": {
          backgroundColor: error ? states.error.background : colorAction.hover.background,
          outline: `${border.width.medium} solid ${semanticColor.focus.outer}`,
          outlineOffset: -1
        },
        ":hover": {
          backgroundColor: error ? states.error.background : colorAction.hover.background,
          outline: `${border.width.medium} solid ${colorAction.hover.border}`,
          outlineOffset: -1
        },
        ":active": {
          backgroundColor: colorAction.press.background,
          outline: `${border.width.medium} solid ${colorAction.press.border}`,
          outlineOffset: -1
        }
      }
    };
  }
  styles$5[styleKey] = StyleSheet.create(newStyles);
  return styles$5[styleKey];
};

const _excluded$5 = ["checked", "description", "disabled", "error", "id", "label", "onChange", "style", "className", "variant"];
const ChoiceInternal = React.forwardRef(function ChoiceInternal(props, ref) {
  const {
      checked,
      description,
      disabled = false,
      error = false,
      id,
      label,
      onChange,
      style,
      className,
      variant
    } = props,
    coreProps = _objectWithoutPropertiesLoose(props, _excluded$5);
  const handleClick = () => {
    if (variant === "radio" && checked) {
      return;
    }
    onChange(!checked);
  };
  const getChoiceCoreComponent = () => {
    if (variant === "radio") {
      return RadioCore;
    } else {
      return CheckboxCore;
    }
  };
  const getLabel = id => {
    return React.createElement(LabelMedium, {
      style: [styles$4.label, disabled && styles$4.disabledLabel]
    }, React.createElement("label", {
      htmlFor: id
    }, label));
  };
  const getDescription = id => {
    return React.createElement(LabelSmall, {
      style: styles$4.description,
      id: id
    }, description);
  };
  const ChoiceCore = getChoiceCoreComponent();
  return (React.createElement(Id, {
      id: id
    }, uniqueId => {
      const descriptionId = description ? `${uniqueId}-description` : undefined;
      return React.createElement(View, {
        style: style,
        className: className
      }, React.createElement(View, {
        style: styles$4.wrapper,
        tabIndex: -1
      }, React.createElement(ChoiceCore, _extends({}, coreProps, {
        id: uniqueId,
        checked: checked,
        "aria-describedby": descriptionId,
        onClick: handleClick,
        disabled: disabled,
        error: error,
        ref: ref
      })), React.createElement(Strut, {
        size: spacing.xSmall_8
      }), label && getLabel(uniqueId)), description && getDescription(descriptionId));
    })
  );
});
const styles$4 = StyleSheet.create({
  wrapper: {
    flexDirection: "row",
    alignItems: "flex-start",
    outline: "none"
  },
  label: {
    marginTop: -2
  },
  disabledLabel: {
    color: semanticColor.action.secondary.disabled.foreground
  },
  description: {
    marginLeft: spacing.medium_16 + spacing.xSmall_8,
    marginTop: spacing.xxxSmall_4,
    color: semanticColor.text.secondary
  }
});

const Checkbox = React.forwardRef(function Checkbox(props, ref) {
  const {
    disabled = false,
    error = false
  } = props;
  return React.createElement(ChoiceInternal, _extends({}, props, {
    variant: "checkbox",
    disabled: disabled,
    error: error,
    ref: ref
  }));
});

const _excluded$4 = ["disabled", "error"];
const Radio = React.forwardRef(function Radio(props, ref) {
  const {
      disabled = false,
      error = false
    } = props,
    otherProps = _objectWithoutPropertiesLoose(props, _excluded$4);
  return React.createElement(ChoiceInternal, _extends({}, otherProps, {
    variant: "radio",
    disabled: disabled,
    error: error,
    ref: ref
  }));
});

const _excluded$3 = ["checked", "disabled", "onChange", "value", "variant"];
const Choice = React.forwardRef(function Choice(props, ref) {
  const {
      checked = false,
      disabled = false,
      onChange = () => {},
      variant
    } = props,
    remainingProps = _objectWithoutPropertiesLoose(props, _excluded$3);
  const getChoiceComponent = variant => {
    if (variant === "checkbox") {
      return Checkbox;
    } else {
      return Radio;
    }
  };
  const ChoiceComponent = getChoiceComponent(variant);
  return React.createElement(ChoiceComponent, _extends({}, remainingProps, {
    checked: checked,
    disabled: disabled,
    onChange: onChange,
    ref: ref
  }));
});

const styles$3 = StyleSheet.create({
  fieldset: {
    display: "flex",
    flexDirection: "column",
    border: "none",
    padding: 0,
    margin: 0
  },
  legend: {
    padding: 0,
    width: "100%"
  },
  description: {
    marginTop: spacing.xxxSmall_4,
    color: semanticColor.text.secondary
  },
  error: {
    marginTop: spacing.xxxSmall_4,
    color: semanticColor.status.critical.foreground
  },
  defaultLineGap: {
    marginTop: spacing.xSmall_8
  }
});

const StyledFieldset$1 = addStyle("fieldset");
const StyledLegend$1 = addStyle("legend");
const CheckboxGroup = React.forwardRef(function CheckboxGroup(props, ref) {
  const {
    children,
    label,
    description,
    errorMessage,
    groupName,
    onChange,
    selectedValues,
    style,
    testId
  } = props;
  const handleChange = (changedValue, originalCheckedState) => {
    if (originalCheckedState) {
      const index = selectedValues.indexOf(changedValue);
      const updatedSelection = [...selectedValues.slice(0, index), ...selectedValues.slice(index + 1)];
      onChange(updatedSelection);
    } else {
      onChange([...selectedValues, changedValue]);
    }
  };
  const allChildren = React.Children.toArray(children).filter(Boolean);
  return React.createElement(StyledFieldset$1, {
    "data-testid": testId,
    style: [styles$3.fieldset, style],
    ref: ref
  }, label && React.createElement(StyledLegend$1, {
    style: styles$3.legend
  }, React.createElement(LabelMedium, null, label)), description && React.createElement(LabelSmall, {
    style: styles$3.description
  }, description), errorMessage && React.createElement(LabelSmall, {
    style: styles$3.error
  }, errorMessage), (label || description || errorMessage) && React.createElement(Strut, {
    size: spacing.small_12
  }), allChildren.map((child, index) => {
    const {
      style,
      value
    } = child.props;
    const checked = selectedValues.includes(value);
    return React.cloneElement(child, {
      checked: checked,
      error: !!errorMessage,
      groupName: groupName,
      id: `${groupName}-${value}`,
      key: value,
      onChange: () => handleChange(value, checked),
      style: [index > 0 && styles$3.defaultLineGap, style],
      variant: "checkbox"
    });
  }));
});

const StyledFieldset = addStyle("fieldset");
const StyledLegend = addStyle("legend");
const RadioGroup = React.forwardRef(function RadioGroup(props, ref) {
  const {
    children,
    label,
    description,
    errorMessage,
    groupName,
    onChange,
    selectedValue,
    style,
    testId
  } = props;
  const allChildren = React.Children.toArray(children).filter(Boolean);
  return React.createElement(StyledFieldset, {
    "data-testid": testId,
    style: [styles$3.fieldset, style],
    ref: ref
  }, label && React.createElement(StyledLegend, {
    style: styles$3.legend
  }, React.createElement(LabelMedium, null, label)), description && React.createElement(LabelSmall, {
    style: styles$3.description
  }, description), errorMessage && React.createElement(LabelSmall, {
    style: styles$3.error
  }, errorMessage), (label || description || errorMessage) && React.createElement(Strut, {
    size: spacing.small_12
  }), allChildren.map((child, index) => {
    const {
      style,
      value
    } = child.props;
    const checked = selectedValue === value;
    return React.cloneElement(child, {
      checked: checked,
      error: !!errorMessage,
      groupName: groupName,
      id: `${groupName}-${value}`,
      key: value,
      onChange: () => onChange(value),
      style: [index > 0 && styles$3.defaultLineGap, style],
      variant: "radio"
    });
  }));
});

const defaultErrorMessage = "This field is required.";
const useFieldValidation = ({
  value,
  disabled: _disabled = false,
  validate,
  onValidate,
  required: _required = false,
  instantValidation: _instantValidation = true
}) => {
  const [errorMessage, setErrorMessage] = React.useState(() => validate && value !== "" && !_disabled && validate(value) || null);
  const onChangeValidation = newValue => {
    if (_instantValidation) {
      handleValidation(newValue);
    } else {
      setErrorMessage(null);
      if (onValidate) {
        onValidate(null);
      }
    }
  };
  const onBlurValidation = newValue => {
    if (!_instantValidation) {
      if (newValue || _required) {
        handleValidation(newValue);
      }
    }
  };
  const handleValidation = newValue => {
    if (_disabled) {
      return;
    }
    if (validate) {
      const error = validate(newValue) || null;
      setErrorMessage(error);
      if (onValidate) {
        onValidate(error);
      }
    } else if (_required) {
      const requiredString = typeof _required === "string" ? _required : defaultErrorMessage;
      const error = newValue ? null : requiredString;
      setErrorMessage(error);
      if (onValidate) {
        onValidate(error);
      }
    }
  };
  useOnMountEffect(() => {
    if (value !== "") {
      handleValidation(value);
    }
  });
  return {
    errorMessage,
    onBlurValidation,
    onChangeValidation
  };
};

const _excluded$2 = ["id", "type", "value", "name", "disabled", "error", "validate", "onValidate", "required", "placeholder", "style", "testId", "readOnly", "autoFocus", "autoComplete", "forwardedRef", "instantValidation", "onKeyDown", "onChange", "onFocus", "onBlur"];
const StyledInput = addStyle("input");
const TextField = props => {
  const {
      id,
      type = "text",
      value,
      name,
      disabled = false,
      error,
      validate,
      onValidate,
      required,
      placeholder,
      style,
      testId,
      readOnly,
      autoFocus,
      autoComplete,
      forwardedRef,
      instantValidation = true,
      onKeyDown,
      onChange,
      onFocus,
      onBlur
    } = props,
    otherProps = _objectWithoutPropertiesLoose(props, _excluded$2);
  const {
    errorMessage,
    onBlurValidation,
    onChangeValidation
  } = useFieldValidation({
    value,
    required,
    disabled,
    instantValidation,
    validate,
    onValidate
  });
  const hasError = error || !!errorMessage;
  const handleChange = event => {
    const newValue = event.target.value;
    onChangeValidation(newValue);
    onChange(newValue);
  };
  const handleFocus = event => {
    if (onFocus) {
      onFocus(event);
    }
  };
  const handleBlur = event => {
    onBlurValidation(event.target.value);
    if (onBlur) {
      onBlur(event);
    }
  };
  return React.createElement(Id, {
    id: id
  }, uniqueId => React.createElement(StyledInput, _extends({
    style: [styles$2.input, styles$7.LabelMedium, styles$2.default, !disabled && styles$2.defaultFocus, disabled && styles$2.disabled, hasError && styles$2.error, style],
    id: uniqueId,
    type: type,
    placeholder: placeholder,
    value: value,
    name: name,
    "aria-disabled": disabled,
    "aria-required": !!required,
    onChange: handleChange,
    onKeyDown: disabled ? undefined : onKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
    "data-testid": testId,
    readOnly: readOnly || disabled,
    autoFocus: autoFocus,
    autoComplete: autoComplete,
    ref: forwardedRef,
    "aria-invalid": hasError
  }, otherProps)));
};
const states$1 = {
  default: {
    border: semanticColor.border.strong,
    background: semanticColor.surface.primary,
    foreground: semanticColor.text.primary
  },
  disabled: {
    border: semanticColor.action.secondary.disabled.border,
    background: semanticColor.action.secondary.disabled.background,
    foreground: semanticColor.text.secondary
  },
  error: {
    border: semanticColor.status.critical.foreground,
    background: semanticColor.status.critical.background,
    foreground: semanticColor.text.primary
  }
};
const styles$2 = StyleSheet.create({
  input: {
    width: "100%",
    height: 40,
    borderRadius: border.radius.radius_040,
    boxSizing: "border-box",
    paddingLeft: spacing.medium_16,
    margin: 0
  },
  default: {
    background: states$1.default.background,
    border: `${border.width.thin} solid ${states$1.default.border}`,
    color: states$1.default.foreground,
    "::placeholder": {
      color: semanticColor.text.secondary
    }
  },
  defaultFocus: {
    ":focus-visible": {
      borderColor: semanticColor.focus.outer,
      outline: `${border.width.thin} solid ${semanticColor.focus.outer}`,
      outlineOffset: -2
    }
  },
  error: {
    background: states$1.error.background,
    border: `${border.width.thin} solid ${states$1.error.border}`,
    color: states$1.error.foreground,
    "::placeholder": {
      color: semanticColor.text.secondary
    },
    ":focus-visible": {
      outlineColor: semanticColor.focus.outer,
      outline: `${border.width.medium} solid ${semanticColor.focus.outer}`
    }
  },
  disabled: {
    background: states$1.disabled.background,
    border: `${border.width.thin} solid ${states$1.disabled.border}`,
    color: states$1.disabled.foreground,
    "::placeholder": {
      color: states$1.disabled.foreground
    },
    cursor: "not-allowed",
    ":focus-visible": {
      outline: `${border.width.medium} solid ${semanticColor.focus.outer}`,
      outlineOffset: -3
    }
  }
});
var TextField$1 = React.forwardRef((props, ref) => React.createElement(TextField, _extends({}, props, {
  forwardedRef: ref
})));

const StyledSpan = addStyle("span");
class FieldHeading extends React.Component {
  renderLabel() {
    const {
      label,
      id,
      required,
      testId
    } = this.props;
    const requiredIcon = React.createElement(StyledSpan, {
      style: styles$1.required,
      "aria-hidden": true
    }, " ", "*");
    return React.createElement(React.Fragment, null, React.createElement(LabelMedium, {
      style: styles$1.label,
      tag: "label",
      htmlFor: id && `${id}-field`,
      testId: testId && `${testId}-label`
    }, label, required && requiredIcon), React.createElement(Strut, {
      size: spacing.xxxSmall_4
    }));
  }
  maybeRenderDescription() {
    const {
      description,
      testId
    } = this.props;
    if (!description) {
      return null;
    }
    return React.createElement(React.Fragment, null, React.createElement(LabelSmall, {
      style: styles$1.description,
      testId: testId && `${testId}-description`
    }, description), React.createElement(Strut, {
      size: spacing.xxxSmall_4
    }));
  }
  maybeRenderError() {
    const {
      error,
      id,
      testId
    } = this.props;
    if (!error) {
      return null;
    }
    return React.createElement(React.Fragment, null, React.createElement(Strut, {
      size: spacing.small_12
    }), React.createElement(LabelSmall, {
      style: styles$1.error,
      role: "alert",
      id: id && `${id}-error`,
      testId: testId && `${testId}-error`
    }, error));
  }
  render() {
    const {
      field,
      style
    } = this.props;
    return React.createElement(View, {
      style: style
    }, this.renderLabel(), this.maybeRenderDescription(), React.createElement(Strut, {
      size: spacing.xSmall_8
    }), field, this.maybeRenderError());
  }
}
const styles$1 = StyleSheet.create({
  label: {
    color: semanticColor.text.primary
  },
  description: {
    color: semanticColor.text.secondary
  },
  error: {
    color: semanticColor.status.critical.foreground
  },
  required: {
    color: semanticColor.status.critical.foreground
  }
});

const _excluded$1 = ["id", "type", "label", "description", "value", "disabled", "required", "validate", "onChange", "onKeyDown", "placeholder", "style", "testId", "readOnly", "autoComplete", "forwardedRef", "ariaDescribedby", "name", "onValidate", "onFocus", "onBlur"];
class LabeledTextField extends React.Component {
  constructor(props) {
    super(props);
    this.handleValidate = errorMessage => {
      const {
        onValidate
      } = this.props;
      this.setState({
        error: errorMessage
      }, () => {
        if (onValidate) {
          onValidate(errorMessage);
        }
      });
    };
    this.handleFocus = event => {
      const {
        onFocus
      } = this.props;
      this.setState({
        focused: true
      }, () => {
        if (onFocus) {
          onFocus(event);
        }
      });
    };
    this.handleBlur = event => {
      const {
        onBlur
      } = this.props;
      this.setState({
        focused: false
      }, () => {
        if (onBlur) {
          onBlur(event);
        }
      });
    };
    this.state = {
      error: null,
      focused: false
    };
  }
  render() {
    const _this$props = this.props,
      {
        id,
        type,
        label,
        description,
        value,
        disabled,
        required,
        validate,
        onChange,
        onKeyDown,
        placeholder,
        style,
        testId,
        readOnly,
        autoComplete,
        forwardedRef,
        ariaDescribedby,
        name
      } = _this$props,
      otherProps = _objectWithoutPropertiesLoose(_this$props, _excluded$1);
    return React.createElement(Id, {
      id: id
    }, uniqueId => React.createElement(FieldHeading, {
      id: uniqueId,
      testId: testId,
      style: style,
      field: React.createElement(TextField$1, _extends({
        id: `${uniqueId}-field`,
        "aria-describedby": ariaDescribedby ? ariaDescribedby : `${uniqueId}-error`,
        "aria-required": required ? "true" : "false",
        required: required,
        testId: testId && `${testId}-field`,
        type: type,
        value: value,
        placeholder: placeholder,
        disabled: disabled,
        validate: validate,
        onValidate: this.handleValidate,
        onChange: onChange,
        onKeyDown: onKeyDown,
        onFocus: this.handleFocus,
        onBlur: this.handleBlur,
        readOnly: readOnly,
        autoComplete: autoComplete,
        ref: forwardedRef,
        name: name
      }, otherProps)),
      label: label,
      description: description,
      required: !!required,
      error: !this.state.focused && this.state.error || ""
    }));
  }
}
LabeledTextField.defaultProps = {
  type: "text",
  disabled: false
};
var labeledTextField = React.forwardRef((props, ref) => React.createElement(LabeledTextField, _extends({}, props, {
  forwardedRef: ref
})));

const _excluded = ["onChange", "value", "placeholder", "disabled", "id", "testId", "style", "readOnly", "autoComplete", "name", "className", "autoFocus", "rows", "spellCheck", "wrap", "minLength", "maxLength", "onClick", "onKeyDown", "onKeyUp", "onFocus", "onBlur", "validate", "onValidate", "required", "resizeType", "rootStyle", "error", "instantValidation"];
const StyledTextarea = addStyle("textarea");
const TextArea = React.forwardRef(function TextArea(props, ref) {
  const {
      onChange,
      value,
      placeholder,
      disabled,
      id,
      testId,
      style,
      readOnly,
      autoComplete,
      name,
      className,
      autoFocus,
      rows,
      spellCheck,
      wrap,
      minLength,
      maxLength,
      onClick,
      onKeyDown,
      onKeyUp,
      onFocus,
      onBlur,
      validate,
      onValidate,
      required,
      resizeType,
      rootStyle,
      error,
      instantValidation = true
    } = props,
    otherProps = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    errorMessage,
    onBlurValidation,
    onChangeValidation
  } = useFieldValidation({
    value,
    disabled,
    validate,
    onValidate,
    required,
    instantValidation
  });
  const hasError = error || !!errorMessage;
  const generatedUniqueId = useId();
  const uniqueId = id != null ? id : generatedUniqueId;
  const handleChange = event => {
    const newValue = event.target.value;
    onChangeValidation(newValue);
    onChange(newValue);
  };
  const handleBlur = event => {
    onBlurValidation(event.target.value);
    if (onBlur) {
      onBlur(event);
    }
  };
  return React.createElement(View, {
    style: [{
      width: "100%"
    }, rootStyle]
  }, React.createElement(StyledTextarea, _extends({
    id: uniqueId,
    "data-testid": testId,
    ref: ref,
    className: className,
    style: [styles.textarea, styles$7.LabelMedium, resizeType && resizeStyles[resizeType], styles.default, !disabled && styles.defaultFocus, disabled && styles.disabled, hasError && styles.error, style],
    value: value,
    onChange: handleChange,
    placeholder: placeholder,
    "aria-disabled": disabled,
    readOnly: readOnly || disabled,
    autoComplete: autoComplete,
    name: name,
    autoFocus: autoFocus,
    rows: rows,
    spellCheck: spellCheck,
    wrap: wrap,
    minLength: minLength,
    maxLength: maxLength,
    "aria-required": !!required,
    onClick: disabled ? undefined : onClick,
    onKeyDown: disabled ? undefined : onKeyDown,
    onKeyUp: disabled ? undefined : onKeyUp,
    onFocus: onFocus,
    onBlur: handleBlur,
    required: !!required
  }, otherProps, {
    "aria-invalid": hasError
  })));
});
const VERTICAL_SPACING_PX = 10;
const states = {
  default: {
    border: semanticColor.border.strong,
    background: semanticColor.surface.primary,
    foreground: semanticColor.text.primary
  },
  disabled: {
    border: semanticColor.action.secondary.disabled.border,
    background: semanticColor.action.secondary.disabled.background,
    foreground: semanticColor.text.secondary
  },
  error: {
    border: semanticColor.status.critical.foreground,
    background: semanticColor.status.critical.background,
    foreground: semanticColor.text.primary
  }
};
const styles = StyleSheet.create({
  textarea: {
    borderRadius: border.radius.radius_040,
    boxSizing: "border-box",
    padding: `${VERTICAL_SPACING_PX}px ${spacing.medium_16}px`,
    minHeight: `${VERTICAL_SPACING_PX * 2 + font.lineHeight.medium + 2}px`
  },
  default: {
    background: states.default.background,
    border: `${border.width.thin} solid ${states.default.border}`,
    color: states.default.foreground,
    "::placeholder": {
      color: semanticColor.text.secondary
    }
  },
  defaultFocus: {
    ":focus-visible": {
      borderColor: semanticColor.focus.outer,
      outline: `${border.width.thin} solid ${semanticColor.focus.outer}`,
      outlineOffset: -2
    }
  },
  disabled: {
    background: states.disabled.background,
    border: `${border.width.thin} solid ${states.disabled.border}`,
    color: states.disabled.foreground,
    "::placeholder": {
      color: states.disabled.foreground
    },
    cursor: "not-allowed",
    ":focus-visible": {
      outline: `${border.width.medium} solid ${semanticColor.focus.outer}`,
      outlineOffset: -3
    }
  },
  error: {
    background: states.error.background,
    border: `${border.width.thin} solid ${states.error.border}`,
    color: states.error.foreground,
    "::placeholder": {
      color: semanticColor.text.secondary
    },
    ":focus-visible": {
      outline: `${border.width.medium} solid ${semanticColor.focus.outer}`,
      borderColor: states.error.border
    }
  }
});
const resizeStyles = StyleSheet.create({
  both: {
    resize: "both"
  },
  none: {
    resize: "none"
  },
  horizontal: {
    resize: "horizontal"
  },
  vertical: {
    resize: "vertical"
  }
});

export { Checkbox, CheckboxGroup, Choice, labeledTextField as LabeledTextField, RadioGroup, TextArea, TextField$1 as TextField };
